import { concatLatestFrom } from '@ngrx/operators';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, repeat, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { ProjectApiService } from '../../services/project-api.service';
import { AppState } from '../app-state';
import { ProjectDto, ProjectStatusDto } from '../../pages/webapp/projects/projects.interface';
import { NotificationsService } from '../../services/notifications.service';
import { selectAllProjects } from './projects.selectors';
import { projectActions } from './projects.actions';
import { invitesSidebarActions } from '@app/pages/webapp/contacts/invites-sidebar/invites-sidebar.actions';

@Injectable()
export class ProjectsEffects {
  private store = inject(Store<AppState>);
  private actions = inject(Actions);
  private projectsApi = inject(ProjectApiService);
  private notif = inject(NotificationsService);

  // it doesn't load if there are projects
  loadProjects$ = createEffect(() => {
    return this.actions.pipe(
      ofType(projectActions.loadProjects),
      concatLatestFrom(() => this.store.select(selectAllProjects)),
      switchMap(([_, projects]) => {
        if (!projects || projects.length === 0) {
          // it doesn't load if there are projects
          return this.projectsApi.getProjects();
        }
        return of(null);
      }),
      map((projects) => {
        if (projects) {
          return projectActions.allProjectsLoadedSuccessfully({ projects });
        }
        return projectActions.allProjectLoadCancelled();
      }),
    );
  });

  notifyOnFailure = createEffect(
    () => {
      return this.actions.pipe(
        ofType(projectActions.updateProjectFailure, projectActions.projectCreationCancelled),
        map((action) => {
          this.notif.showError(action.errorMessage ?? 'Failed to update project');
        }),
      );
    },
    {
      dispatch: false,
    },
  );

  loadSimpleProjectsOnLoadInvitesSidebar$ = createEffect(() => {
    return this.actions.pipe(
      ofType(invitesSidebarActions.loadInvitesSidebar),
      map(() => {
        return projectActions.loadProjectsSimple();
      }),
    );
  });

  loadProjectsSimple$ = createEffect(() => {
    return this.actions.pipe(
      ofType(projectActions.loadProjectsSimple),
      switchMap((action) => {
        return this.projectsApi.getSimpleProjects();
      }),
      catchError((err) => {
        console.warn('loadProjectsSimple$ err on load', err);
        this.notif.showError('Failed to load projects data: ' + err?.error?.message);
        return of(null);
      }),
      map((projects) => {
        if (!projects) {
          return projectActions.simpleProjectLoadCancelled();
        }
        return projectActions.simpleProjectsLoadedSuccessfully({ projects });
      }),
    );
  });

  loadBackendStatuses = createEffect(() => {
    return this.actions.pipe(
      ofType(projectActions.loadBackendStatuses),
      switchMap((_) => {
        return this.projectsApi.getPossibleStatuses$();
      }),
      catchError((err) => {
        console.warn('err on load', err);
        return of(null);
      }),
      map((statuses: ProjectStatusDto[] | null) => {
        if (!statuses || !statuses?.length) {
          return projectActions.cancelStatusLoading();
        }
        statuses.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });
        return projectActions.statusesLoadedSuccessfully({ statuses });
      }),
    );
  });

  createProject = createEffect(() => {
    return this.actions.pipe(
      ofType(projectActions.createProject),
      switchMap((action) => {
        return this.projectsApi.createProject$(action.project);
      }),
      map((project: ProjectDto) => {
        return projectActions.projectCreatedSuccessfully({ projectId: project.id });
      }),
      catchError((err: any) => {
        console.warn('err on project create', err);
        return of(
          projectActions.projectCreationCancelled({
            errorMessage: err?.error?.message ?? 'Failed to create project',
          }),
        );
      }),
      repeat(),
    );
  });

  editProjectInitiated = createEffect(() => {
    return this.actions.pipe(
      ofType(projectActions.editProjectInitiated),
      switchMap((action) => {
        return this.projectsApi.getProjectById$(action.id);
      }),
      map((project: ProjectDto) => {
        if (!project?.id) {
          return projectActions.failedToInitiateProjectEdit();
        }
        return projectActions.editProjectDataLoaded({ project });
      }),
    );
  });

  deleteProject = createEffect(() => {
    return this.actions.pipe(
      ofType(projectActions.deleteProject),
      exhaustMap((action) => {
        return this.projectsApi.deleteProject$(action.id);
      }),
      catchError((err) => {
        this.notif.showError(err?.error?.message || 'Failed to delete project');
        console.warn('err on load', err);
        return of(null);
      }),
      map((data) => {
        if (!data) {
          this.notif.showError('Failed to delete project');
          return projectActions.failedToDeleteProject();
        }
        return projectActions.successfulProjectDeletion();
      }),
    );
  });

  updateProject = createEffect(() => {
    return this.actions.pipe(
      ofType(projectActions.updateProject),
      switchMap((action) => {
        return this.projectsApi.updateProject$(action.project);
      }),
      map((_) => {
        return projectActions.updateProjectSuccess();
      }),
      catchError((err) => {
        console.warn('err on project update', err);
        return of(
          projectActions.updateProjectFailure({
            errorMessage: err?.error?.message ?? 'Failed to update project',
          }),
        );
      }),
      repeat(),
    );
  });
}
